/**
 * Shopify E-commerce Integration
 * Implements comprehensive Shopify integration with OAuth app installation flow,
 * customer and order synchronization, product catalog sync, and cart abandonment handling
 */

import { BaseEcommerceIntegration } from '../base/BaseEcommerceIntegration';
import {
  IntegrationConfig,
  OAuthTokens,
  ValidationResult,
  TestResult,
  ConnectionResult
} from '../../../types/integration';
import {
  EcommerceProduct,
  EcommerceCustomer,
  EcommerceOrder,
  CartAbandonmentEvent,
  RecommendationContext
} from '../base/BaseEcommerceIntegration';
import { APIGateway } from '../../api/APIGateway';
import { AuthenticationService } from '../../api/AuthenticationService';

export interface ShopifyConfig extends IntegrationConfig {
  shopDomain: string;
  accessToken?: string;
  apiKey?: string;
  apiSecret?: string;
  webhookSecret?: string;
  webhookEndpoint?: string;
  scopes: string[];
  usePrivateApp: boolean;
  apiVersion: string;
}

export interface ShopifyTokens extends OAuthTokens {
  shop: string;
  scope: string;
}

export interface ShopifyProduct {
  id: number;
  title: string;
  body_html?: string;
  vendor: string;
  product_type: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
  template_suffix?: string;
  status: 'active' | 'archived' | 'draft';
  published_scope: string;
  tags: string;
  admin_graphql_api_id: string;
  variants: ShopifyVariant[];
  options: ShopifyOption[];
  images: ShopifyImage[];
  image?: ShopifyImage;
  handle: string;
  metafields?: ShopifyMetafield[];
}

export interface ShopifyVariant {
  id: number;
  product_id: number;
  title: string;
  price: string;
  sku?: string;
  position: number;
  inventory_policy: 'deny' | 'continue';
  compare_at_price?: string;
  fulfillment_service: string;
  inventory_management?: string;
  option1?: string;
  option2?: string;
  option3?: string;
  created_at: string;
  updated_at: string;
  taxable: boolean;
  barcode?: string;
  grams: number;
  image_id?: number;
  weight: number;
  weight_unit: string;
  inventory_item_id: number;
  inventory_quantity: number;
  old_inventory_quantity: number;
  requires_shipping: boolean;
  admin_graphql_api_id: string;
}

export interface ShopifyOption {
  id: number;
  product_id: number;
  name: string;
  position: number;
  values: string[];
}

export interface ShopifyImage {
  id: number;
  product_id: number;
  position: number;
  created_at: string;
  updated_at: string;
  alt?: string;
  width: number;
  height: number;
  src: string;
  variant_ids: number[];
  admin_graphql_api_id: string;
}

export interface ShopifyCustomer {
  id: number;
  email: string;
  accepts_marketing: boolean;
  created_at: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  orders_count: number;
  state: 'disabled' | 'invited' | 'enabled' | 'declined';
  total_spent: string;
  last_order_id?: number;
  note?: string;
  verified_email: boolean;
  multipass_identifier?: string;
  tax_exempt: boolean;
  phone?: string;
  tags: string;
  last_order_name?: string;
  currency: string;
  addresses: ShopifyAddress[];
  accepts_marketing_updated_at: string;
  marketing_opt_in_level?: string;
  tax_exemptions: string[];
  admin_graphql_api_id: string;
  default_address?: ShopifyAddress;
  metafields?: ShopifyMetafield[];
}

export interface ShopifyAddress {
  id: number;
  customer_id: number;
  first_name?: string;
  last_name?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  province?: string;
  country: string;
  zip: string;
  phone?: string;
  name: string;
  province_code?: string;
  country_code: string;
  country_name: string;
  default: boolean;
}

export interface ShopifyOrder {
  id: number;
  admin_graphql_api_id: string;
  app_id?: number;
  browser_ip?: string;
  buyer_accepts_marketing: boolean;
  cancel_reason?: string;
  cancelled_at?: string;
  cart_token?: string;
  checkout_id?: number;
  checkout_token?: string;
  client_details?: any;
  closed_at?: string;
  confirmed: boolean;
  contact_email?: string;
  created_at: string;
  currency: string;
  current_subtotal_price: string;
  current_subtotal_price_set: any;
  current_total_discounts: string;
  current_total_discounts_set: any;
  current_total_duties_set?: any;
  current_total_price: string;
  current_total_price_set: any;
  current_total_tax: string;
  current_total_tax_set: any;
  customer_locale?: string;
  device_id?: number;
  discount_codes: any[];
  email: string;
  estimated_taxes: boolean;
  financial_status: 'pending' | 'authorized' | 'partially_paid' | 'paid' | 'partially_refunded' | 'refunded' | 'voided';
  fulfillment_status?: 'fulfilled' | 'null' | 'partial' | 'restocked';
  gateway?: string;
  landing_site?: string;
  landing_site_ref?: string;
  location_id?: number;
  name: string;
  note?: string;
  note_attributes: any[];
  number: number;
  order_number: number;
  order_status_url: string;
  original_total_duties_set?: any;
  payment_gateway_names: string[];
  phone?: string;
  presentment_currency: string;
  processed_at: string;
  processing_method: string;
  reference?: string;
  referring_site?: string;
  source_identifier?: string;
  source_name: string;
  source_url?: string;
  subtotal_price: string;
  subtotal_price_set: any;
  tags: string;
  tax_lines: any[];
  taxes_included: boolean;
  test: boolean;
  token: string;
  total_discounts: string;
  total_discounts_set: any;
  total_line_items_price: string;
  total_line_items_price_set: any;
  total_outstanding: string;
  total_price: string;
  total_price_set: any;
  total_price_usd: string;
  total_shipping_price_set: any;
  total_tax: string;
  total_tax_set: any;
  total_tip_received: string;
  total_weight: number;
  updated_at: string;
  user_id?: number;
  billing_address?: ShopifyAddress;
  customer?: ShopifyCustomer;
  discount_applications: any[];
  fulfillments: any[];
  line_items: ShopifyLineItem[];
  payment_terms?: any;
  refunds: any[];
  shipping_address?: ShopifyAddress;
  shipping_lines: any[];
}

export interface ShopifyLineItem {
  id: number;
  admin_graphql_api_id: string;
  fulfillable_quantity: number;
  fulfillment_service: string;
  fulfillment_status?: string;
  gift_card: boolean;
  grams: number;
  name: string;
  origin_location?: any;
  price: string;
  price_set: any;
  product_exists: boolean;
  product_id?: number;
  properties: any[];
  quantity: number;
  requires_shipping: boolean;
  sku?: string;
  taxable: boolean;
  title: string;
  total_discount: string;
  total_discount_set: any;
  variant_id?: number;
  variant_inventory_management?: string;
  variant_title?: string;
  vendor?: string;
  tax_lines: any[];
  duties: any[];
  discount_allocations: any[];
}

export interface ShopifyMetafield {
  id: number;
  namespace: string;
  key: string;
  value: string;
  value_type: string;
  description?: string;
  owner_id: number;
  created_at: string;
  updated_at: string;
  owner_resource: string;
  type: string;
  admin_graphql_api_id: string;
}

export interface ShopifyCheckout {
  id: number;
  token: string;
  cart_token: string;
  email?: string;
  gateway?: string;
  buyer_accepts_marketing: boolean;
  created_at: string;
  updated_at: string;
  landing_site?: string;
  note?: string;
  note_attributes: any[];
  referring_site?: string;
  shipping_lines: any[];
  taxes_included: boolean;
  total_weight: number;
  currency: string;
  completed_at?: string;
  closed_at?: string;
  user_id?: number;
  location_id?: number;
  source_identifier?: string;
  source_url?: string;
  device_id?: number;
  phone?: string;
  customer_locale?: string;
  line_items: ShopifyLineItem[];
  name: string;
  source: string;
  abandoned_checkout_url: string;
  discount_codes: any[];
  tax_lines: any[];
  source_name: string;
  presentment_currency: string;
  buyer_accepts_sms_marketing: boolean;
  sms_marketing_phone?: string;
  total_discounts: string;
  total_line_items_price: string;
  total_price: string;
  total_tax: string;
  subtotal_price: string;
  billing_address?: ShopifyAddress;
  shipping_address?: ShopifyAddress;
  customer?: ShopifyCustomer;
}

export class ShopifyIntegration extends BaseEcommerceIntegration {
  private apiGateway: APIGateway;
  private authService: AuthenticationService;
  private shopifyConfig: ShopifyConfig;
  private baseUrl: string;

  constructor(config: ShopifyConfig) {
    super(config);
    this.shopifyConfig = config;
    this.apiGateway = APIGateway.getInstance();
    this.authService = new AuthenticationService();
    this.baseUrl = `https://${config.shopDomain}.myshopify.com/admin/api/${config.apiVersion}`;
  }

  /**
   * Validate Shopify configuration
   */
  async validateConfig(): Promise<ValidationResult> {
    const errors: string[] = [];

    if (!this.shopifyConfig.shopDomain) {
      errors.push('Shop domain is required');
    }

    if (this.shopifyConfig.usePrivateApp) {
      if (!this.shopifyConfig.accessToken) {
        errors.push('Access token is required for private app');
      }
    } else {
      if (!this.shopifyConfig.apiKey || !this.shopifyConfig.apiSecret) {
        errors.push('API key and secret are required for OAuth app');
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Test connection to Shopify store
   */
  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    
    try {
      const response = await this.makeRequest('GET', '/shop.json');
      
      if (response.shop) {
        return {
          success: true,
          message: `Successfully connected to ${response.shop.name}`,
          duration: Date.now() - startTime,
          details: {
            shopName: response.shop.name,
            domain: response.shop.domain,
            email: response.shop.email,
            currency: response.shop.currency,
            timezone: response.shop.timezone
          }
        };
      }
      
      return {
        success: false,
        message: 'Failed to retrieve shop information',
        duration: Date.now() - startTime
      };
    } catch (error) {
      return {
        success: false,
        message: 'Connection test failed',
        duration: Date.now() - startTime
      };
    }
  }

  /**
   * Connect to Shopify using OAuth or private app credentials
   */
  async connect(): Promise<ConnectionResult> {
    try {
      if (this.shopifyConfig.usePrivateApp) {
        return await this.connectPrivateApp();
      } else {
        return await this.connectOAuth();
      }
    } catch (error) {
      return {
        success: false,
        message: 'Failed to connect to Shopify'
      };
    }
  }

  /**
   * Disconnect from Shopify
   */
  async disconnect(): Promise<void> {
    try {
      // Unregister webhooks
      await this.unregisterWebhooks();
      
      // Clear stored credentials
      this.shopifyConfig.accessToken = undefined;
      
      await this.emitEvent('shopify.disconnected', {});
    } catch (error) {
      await this.emitEvent('shopify.disconnect_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync products from Shopify
   */
  async syncProducts(): Promise<EcommerceProduct[]> {
    try {
      const products: EcommerceProduct[] = [];
      let hasNextPage = true;
      let pageInfo: string | undefined;

      while (hasNextPage) {
        const response = await this.makeRequest('GET', '/products.json', {
          limit: 250,
          page_info: pageInfo
        });

        if (response.products && Array.isArray(response.products)) {
          for (const shopifyProduct of response.products) {
            const product = this.transformShopifyProduct(shopifyProduct);
            products.push(product);
          }
        }

        // Check for pagination
        const linkHeader = response.headers?.link;
        hasNextPage = linkHeader && linkHeader.includes('rel="next"');
        if (hasNextPage) {
          pageInfo = this.extractPageInfo(linkHeader);
        }
      }

      await this.emitEvent('products.synced', { count: products.length });
      return products;
    } catch (error) {
      await this.emitEvent('products.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync customers from Shopify
   */
  async syncCustomers(): Promise<EcommerceCustomer[]> {
    try {
      const customers: EcommerceCustomer[] = [];
      let hasNextPage = true;
      let pageInfo: string | undefined;

      while (hasNextPage) {
        const response = await this.makeRequest('GET', '/customers.json', {
          limit: 250,
          page_info: pageInfo
        });

        if (response.customers && Array.isArray(response.customers)) {
          for (const shopifyCustomer of response.customers) {
            const customer = this.transformShopifyCustomer(shopifyCustomer);
            customers.push(customer);
          }
        }

        // Check for pagination
        const linkHeader = response.headers?.link;
        hasNextPage = linkHeader && linkHeader.includes('rel="next"');
        if (hasNextPage) {
          pageInfo = this.extractPageInfo(linkHeader);
        }
      }

      await this.emitEvent('customers.synced', { count: customers.length });
      return customers;
    } catch (error) {
      await this.emitEvent('customers.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Sync orders from Shopify
   */
  async syncOrders(dateRange?: { start: Date; end: Date }): Promise<EcommerceOrder[]> {
    try {
      const orders: EcommerceOrder[] = [];
      let hasNextPage = true;
      let pageInfo: string | undefined;

      const params: any = {
        limit: 250,
        status: 'any'
      };

      if (dateRange) {
        params.created_at_min = dateRange.start.toISOString();
        params.created_at_max = dateRange.end.toISOString();
      }

      while (hasNextPage) {
        if (pageInfo) {
          params.page_info = pageInfo;
        }

        const response = await this.makeRequest('GET', '/orders.json', params);

        if (response.orders && Array.isArray(response.orders)) {
          for (const shopifyOrder of response.orders) {
            const order = this.transformShopifyOrder(shopifyOrder);
            orders.push(order);
          }
        }

        // Check for pagination
        const linkHeader = response.headers?.link;
        hasNextPage = linkHeader && linkHeader.includes('rel="next"');
        if (hasNextPage) {
          pageInfo = this.extractPageInfo(linkHeader);
        }
      }

      await this.emitEvent('orders.synced', { count: orders.length });
      return orders;
    } catch (error) {
      await this.emitEvent('orders.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Get a specific product by ID
   */
  async getProduct(productId: string): Promise<EcommerceProduct | null> {
    try {
      const response = await this.makeRequest('GET', `/products/${productId}.json`);
      
      if (response.product) {
        return this.transformShopifyProduct(response.product);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific customer by ID
   */
  async getCustomer(customerId: string): Promise<EcommerceCustomer | null> {
    try {
      const response = await this.makeRequest('GET', `/customers/${customerId}.json`);
      
      if (response.customer) {
        return this.transformShopifyCustomer(response.customer);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get a specific order by ID
   */
  async getOrder(orderId: string): Promise<EcommerceOrder | null> {
    try {
      const response = await this.makeRequest('GET', `/orders/${orderId}.json`);
      
      if (response.order) {
        return this.transformShopifyOrder(response.order);
      }
      
      return null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update inventory for products
   */
  async updateInventory(updates: { productId: string; variantId?: string; inventory: number }[]): Promise<void> {
    try {
      for (const update of updates) {
        if (update.variantId) {
          // Update specific variant inventory
          await this.makeRequest('PUT', `/variants/${update.variantId}.json`, {
            variant: {
              id: parseInt(update.variantId),
              inventory_quantity: update.inventory
            }
          });
        } else {
          // Update all variants of the product
          const product = await this.getProduct(update.productId);
          if (product && product.variants) {
            for (const variant of product.variants) {
              await this.makeRequest('PUT', `/variants/${variant.id}.json`, {
                variant: {
                  id: variant.id,
                  inventory_quantity: update.inventory
                }
              });
            }
          }
        }
      }

      await this.emitEvent('inventory.updated', { updates });
    } catch (error) {
      await this.emitEvent('inventory.update_failed', { 
        updates, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Get product recommendations based on context
   */
  async getRecommendations(context: RecommendationContext): Promise<EcommerceProduct[]> {
    try {
      // This is a simplified implementation
      // In a real scenario, you'd use Shopify's recommendation engine or build your own
      const products = await this.syncProducts();
      
      let recommendations = products;

      // Filter by category if specified
      if (context.category) {
        recommendations = recommendations.filter(product => 
          product.categories.includes(context.category!)
        );
      }

      // Filter by price range if specified
      if (context.priceRange) {
        recommendations = recommendations.filter(product => 
          product.price >= context.priceRange!.min && 
          product.price <= context.priceRange!.max
        );
      }

      // Limit results
      recommendations = recommendations.slice(0, context.limit);

      await this.emitEvent('recommendations.generated', { 
        context, 
        count: recommendations.length 
      });

      return recommendations;
    } catch (error) {
      await this.emitEvent('recommendations.generation_failed', { 
        context, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  /**
   * Handle webhook from Shopify
   */
  async handleWebhook(topic: string, payload: any, headers: Record<string, string>): Promise<void> {
    try {
      // Verify webhook signature
      if (!this.verifyWebhookSignature(payload, headers)) {
        throw new Error('Invalid webhook signature');
      }

      switch (topic) {
        case 'orders/create':
          await this.handleOrderCreated(payload);
          break;
        case 'orders/updated':
          await this.handleOrderUpdated(payload);
          break;
        case 'orders/paid':
          await this.handleOrderPaid(payload);
          break;
        case 'orders/cancelled':
          await this.handleOrderCancelled(payload);
          break;
        case 'customers/create':
          await this.handleCustomerCreated(payload);
          break;
        case 'customers/update':
          await this.handleCustomerUpdated(payload);
          break;
        case 'checkouts/create':
          await this.handleCheckoutCreated(payload);
          break;
        case 'checkouts/update':
          await this.handleCheckoutUpdated(payload);
          break;
        case 'products/create':
          await this.handleProductCreated(payload);
          break;
        case 'products/update':
          await this.handleProductUpdated(payload);
          break;
        case 'inventory_levels/update':
          await this.handleInventoryUpdated(payload);
          break;
        default:
          console.log(`Unhandled webhook topic: ${topic}`);
      }

      await this.emitEvent('webhook.processed', { topic, payload });
    } catch (error) {
      await this.emitEvent('webhook.processing_failed', { 
        topic, 
        payload, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Private helper methods

  private async connectPrivateApp(): Promise<ConnectionResult> {
    if (!this.shopifyConfig.accessToken) {
      return {
        success: false,
        message: 'Access token required for private app connection'
      };
    }

    const testResult = await this.testConnection();
    if (testResult.success) {
      await this.registerWebhooks();
      return {
        success: true,
        message: 'Successfully connected to Shopify using private app'
      };
    }

    return {
      success: false,
      message: 'Failed to connect using private app credentials'
    };
  }

  private async connectOAuth(): Promise<ConnectionResult> {
    // OAuth flow would be implemented here
    // This is a simplified version
    return {
      success: false,
      message: 'OAuth connection not implemented yet',
      error: 'Not implemented'
    };
  }

  private async makeRequest(method: string, endpoint: string, data?: any): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.shopifyConfig.accessToken) {
      headers['X-Shopify-Access-Token'] = this.shopifyConfig.accessToken;
    }

    return await this.apiGateway.processRequest({
      method: method as any,
      url,
      headers,
      body: data,
      timeout: 30000
    });
  }

  private transformShopifyProduct(shopifyProduct: ShopifyProduct): EcommerceProduct {
    return {
      id: shopifyProduct.id.toString(),
      title: shopifyProduct.title,
      description: shopifyProduct.body_html,
      price: shopifyProduct.variants[0]?.price ? parseFloat(shopifyProduct.variants[0].price) : 0,
      compareAtPrice: shopifyProduct.variants[0]?.compare_at_price ? 
        parseFloat(shopifyProduct.variants[0].compare_at_price) : undefined,
      currency: 'USD', // Would need to get from shop settings
      sku: shopifyProduct.variants[0]?.sku,
      inventory: shopifyProduct.variants.reduce((sum, variant) => sum + variant.inventory_quantity, 0),
      images: shopifyProduct.images.map(img => ({
        id: img.id.toString(),
        url: img.src,
        altText: img.alt,
        position: img.position
      })),
      categories: [shopifyProduct.product_type],
      tags: shopifyProduct.tags.split(',').map(tag => tag.trim()),
      variants: shopifyProduct.variants.map(variant => ({
        id: variant.id.toString(),
        title: variant.title,
        price: parseFloat(variant.price),
        sku: variant.sku,
        inventory: variant.inventory_quantity,
        options: {
          option1: variant.option1 || '',
          option2: variant.option2 || '',
          option3: variant.option3 || ''
        }
      })),
      status: shopifyProduct.status,
      createdAt: new Date(shopifyProduct.created_at),
      updatedAt: new Date(shopifyProduct.updated_at),
      customFields: {
        vendor: shopifyProduct.vendor,
        handle: shopifyProduct.handle,
        shopifyId: shopifyProduct.id
      }
    };
  }

  private transformShopifyCustomer(shopifyCustomer: ShopifyCustomer): EcommerceCustomer {
    return {
      id: shopifyCustomer.id.toString(),
      email: shopifyCustomer.email,
      firstName: shopifyCustomer.first_name,
      lastName: shopifyCustomer.last_name,
      phone: shopifyCustomer.phone,
      addresses: shopifyCustomer.addresses.map(addr => ({
        id: addr.id.toString(),
        firstName: addr.first_name,
        lastName: addr.last_name,
        company: addr.company,
        address1: addr.address1,
        address2: addr.address2,
        city: addr.city,
        province: addr.province,
        country: addr.country,
        zip: addr.zip,
        phone: addr.phone,
        isDefault: addr.default
      })),
      totalSpent: parseFloat(shopifyCustomer.total_spent),
      orderCount: shopifyCustomer.orders_count,
      averageOrderValue: shopifyCustomer.orders_count > 0 ? 
        parseFloat(shopifyCustomer.total_spent) / shopifyCustomer.orders_count : 0,
      lastOrderAt: shopifyCustomer.last_order_id ? new Date() : undefined, // Would need to fetch actual date
      tags: shopifyCustomer.tags.split(',').map(tag => tag.trim()),
      acceptsMarketing: shopifyCustomer.accepts_marketing,
      createdAt: new Date(shopifyCustomer.created_at),
      updatedAt: new Date(shopifyCustomer.updated_at),
      customFields: {
        shopifyId: shopifyCustomer.id,
        state: shopifyCustomer.state,
        verifiedEmail: shopifyCustomer.verified_email,
        currency: shopifyCustomer.currency
      }
    };
  }

  private transformShopifyOrder(shopifyOrder: ShopifyOrder): EcommerceOrder {
    return {
      id: shopifyOrder.id.toString(),
      orderNumber: shopifyOrder.name,
      customerId: shopifyOrder.customer?.id.toString() || '',
      email: shopifyOrder.email,
      totalPrice: parseFloat(shopifyOrder.total_price),
      subtotalPrice: parseFloat(shopifyOrder.subtotal_price),
      taxPrice: parseFloat(shopifyOrder.total_tax),
      shippingPrice: shopifyOrder.shipping_lines.reduce((sum: number, line: any) => 
        sum + parseFloat(line.price || '0'), 0),
      currency: shopifyOrder.currency,
      status: this.mapShopifyOrderStatus(shopifyOrder.financial_status, shopifyOrder.fulfillment_status),
      lineItems: shopifyOrder.line_items.map(item => ({
        id: item.id.toString(),
        productId: item.product_id?.toString() || '',
        variantId: item.variant_id?.toString(),
        title: item.title,
        quantity: item.quantity,
        price: parseFloat(item.price),
        totalPrice: parseFloat(item.price) * item.quantity,
        sku: item.sku
      })),
      shippingAddress: shopifyOrder.shipping_address ? {
        id: '0',
        firstName: shopifyOrder.shipping_address.first_name,
        lastName: shopifyOrder.shipping_address.last_name,
        company: shopifyOrder.shipping_address.company,
        address1: shopifyOrder.shipping_address.address1,
        address2: shopifyOrder.shipping_address.address2,
        city: shopifyOrder.shipping_address.city,
        province: shopifyOrder.shipping_address.province,
        country: shopifyOrder.shipping_address.country,
        zip: shopifyOrder.shipping_address.zip,
        phone: shopifyOrder.shipping_address.phone,
        isDefault: false
      } : undefined,
      billingAddress: shopifyOrder.billing_address ? {
        id: '0',
        firstName: shopifyOrder.billing_address.first_name,
        lastName: shopifyOrder.billing_address.last_name,
        company: shopifyOrder.billing_address.company,
        address1: shopifyOrder.billing_address.address1,
        address2: shopifyOrder.billing_address.address2,
        city: shopifyOrder.billing_address.city,
        province: shopifyOrder.billing_address.province,
        country: shopifyOrder.billing_address.country,
        zip: shopifyOrder.billing_address.zip,
        phone: shopifyOrder.billing_address.phone,
        isDefault: false
      } : undefined,
      createdAt: new Date(shopifyOrder.created_at),
      updatedAt: new Date(shopifyOrder.updated_at),
      customFields: {
        shopifyId: shopifyOrder.id,
        orderNumber: shopifyOrder.order_number,
        token: shopifyOrder.token,
        financialStatus: shopifyOrder.financial_status,
        fulfillmentStatus: shopifyOrder.fulfillment_status
      }
    };
  }

  private mapShopifyOrderStatus(financialStatus: string, fulfillmentStatus?: string): 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded' {
    if (financialStatus === 'refunded') return 'refunded';
    if (financialStatus === 'voided') return 'cancelled';
    if (fulfillmentStatus === 'fulfilled') return 'fulfilled';
    if (financialStatus === 'paid') return 'paid';
    return 'pending';
  }

  private extractPageInfo(linkHeader: string): string | undefined {
    const nextMatch = linkHeader.match(/<[^>]*[?&]page_info=([^&>]+)[^>]*>;\s*rel="next"/);
    return nextMatch ? nextMatch[1] : undefined;
  }

  private verifyWebhookSignature(payload: any, headers: Record<string, string>): boolean {
    if (!this.shopifyConfig.webhookSecret) {
      return true; // Skip verification if no secret configured
    }

    const signature = headers['x-shopify-hmac-sha256'];
    if (!signature) {
      return false;
    }

    // Implement HMAC verification
    // This would use crypto to verify the signature
    return true; // Simplified for now
  }

  private async registerWebhooks(): Promise<void> {
    const webhooks = [
      'orders/create',
      'orders/updated',
      'orders/paid',
      'orders/cancelled',
      'customers/create',
      'customers/update',
      'checkouts/create',
      'checkouts/update',
      'products/create',
      'products/update',
      'inventory_levels/update'
    ];

    for (const topic of webhooks) {
      try {
        await this.makeRequest('POST', '/webhooks.json', {
          webhook: {
            topic,
            address: `${this.shopifyConfig.webhookEndpoint || 'https://example.com/webhooks'}/${topic.replace('/', '_')}`,
            format: 'json'
          }
        });
      } catch (error) {
        console.error(`Failed to register webhook for ${topic}:`, error);
      }
    }
  }

  private async unregisterWebhooks(): Promise<void> {
    try {
      const response = await this.makeRequest('GET', '/webhooks.json');
      
      if (response.webhooks) {
        for (const webhook of response.webhooks) {
          await this.makeRequest('DELETE', `/webhooks/${webhook.id}.json`);
        }
      }
    } catch (error) {
      console.error('Failed to unregister webhooks:', error);
    }
  }

  // Webhook handlers

  private async handleOrderCreated(order: ShopifyOrder): Promise<void> {
    const transformedOrder = this.transformShopifyOrder(order);
    await this.handlePurchase(transformedOrder);
  }

  private async handleOrderUpdated(order: ShopifyOrder): Promise<void> {
    const transformedOrder = this.transformShopifyOrder(order);
    await this.emitEvent('order.updated', { order: transformedOrder });
  }

  private async handleOrderPaid(order: ShopifyOrder): Promise<void> {
    const transformedOrder = this.transformShopifyOrder(order);
    await this.handlePurchase(transformedOrder);
  }

  private async handleOrderCancelled(order: ShopifyOrder): Promise<void> {
    const transformedOrder = this.transformShopifyOrder(order);
    await this.emitEvent('order.cancelled', { order: transformedOrder });
  }

  private async handleCustomerCreated(customer: ShopifyCustomer): Promise<void> {
    const transformedCustomer = this.transformShopifyCustomer(customer);
    await this.emitEvent('customer.created', { customer: transformedCustomer });
  }

  private async handleCustomerUpdated(customer: ShopifyCustomer): Promise<void> {
    const transformedCustomer = this.transformShopifyCustomer(customer);
    await this.emitEvent('customer.updated', { customer: transformedCustomer });
  }

  private async handleCheckoutCreated(checkout: ShopifyCheckout): Promise<void> {
    // Handle potential cart abandonment
    setTimeout(async () => {
      try {
        const response = await this.makeRequest('GET', `/checkouts/${checkout.token}.json`);
        if (response.checkout && !response.checkout.completed_at) {
          // Checkout still not completed - trigger abandonment
          const cartEvent: CartAbandonmentEvent = {
            id: checkout.id.toString(),
            customerId: checkout.customer?.id.toString() || '',
            email: checkout.email || '',
            cartToken: checkout.cart_token,
            lineItems: checkout.line_items.map(item => ({
              id: item.id.toString(),
              productId: item.product_id?.toString() || '',
              variantId: item.variant_id?.toString(),
              title: item.title,
              quantity: item.quantity,
              price: parseFloat(item.price),
              totalPrice: parseFloat(item.price) * item.quantity,
              sku: item.sku
            })),
            totalPrice: parseFloat(checkout.total_price),
            currency: checkout.currency,
            abandonedAt: new Date(),
            recoveryUrl: checkout.abandoned_checkout_url
          };

          await this.handleCartAbandonment(cartEvent);
        }
      } catch (error) {
        console.error('Error checking checkout abandonment:', error);
      }
    }, 30 * 60 * 1000); // Check after 30 minutes
  }

  private async handleCheckoutUpdated(checkout: ShopifyCheckout): Promise<void> {
    if (checkout.completed_at) {
      // Checkout completed - no abandonment
      await this.emitEvent('checkout.completed', { checkout });
    }
  }

  private async handleProductCreated(product: ShopifyProduct): Promise<void> {
    const transformedProduct = this.transformShopifyProduct(product);
    await this.emitEvent('product.created', { product: transformedProduct });
  }

  private async handleProductUpdated(product: ShopifyProduct): Promise<void> {
    const transformedProduct = this.transformShopifyProduct(product);
    await this.emitEvent('product.updated', { product: transformedProduct });
    
    // Check inventory changes
    for (const variant of product.variants) {
      await this.handleInventoryChange(
        product.id.toString(),
        variant.id.toString(),
        variant.inventory_quantity
      );
    }
  }

  private async handleInventoryUpdated(inventoryLevel: any): Promise<void> {
    await this.emitEvent('inventory.updated', { inventoryLevel });
    
    // Handle inventory changes for campaigns
    if (inventoryLevel.available !== undefined) {
      await this.handleInventoryChange(
        inventoryLevel.inventory_item_id?.toString() || '',
        undefined,
        inventoryLevel.available
      );
    }
  }
}